import React, { useState, useEffect } from 'react';
import { ClaimCommentsInbox } from '@/components/admin/ClaimCommentsInbox';
import { PilotSignupsInbox } from '@/components/admin/PilotSignupsInbox';
import { motion } from 'framer-motion';
import { Navbar, Footer } from '@/components/Layout';
import { useAuth } from '@/context/AuthContext';
import { FeeRevenuePanel } from '@/components/admin/FeeRevenuePanel';
import { ClaimDecisionPanel } from '@/components/admin/ClaimDecisionPanel';
import { BreachRecordAdminPanel } from '@/components/admin/BreachRecordAdminPanel';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Users, Coins, FileText, TrendingUp, Vote, Bell,
  Shield, Trash2, ChevronDown, Activity, BarChart3,
  UserCheck, UserX, Clock, DollarSign, AlertTriangle,
  Download, Lock, LogOut, ShieldAlert, Eye, Settings, KeyRound, CheckCircle2, Share2, ExternalLink, Copy, Megaphone,
  Home, ChevronUp, Loader2, Ban, PiggyBank, Bot, Layers, CreditCard, Package
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

function StatCard({ label, value, sub, icon: Icon, color = 'text-blue-400', delay = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      className="bg-white/[0.03] border border-white/5 rounded-xl p-5"
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-3">
        <Icon className={`w-5 h-5 ${color}`} />
        {sub && <span className="text-xs text-gray-600">{sub}</span>}
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
    </motion.div>
  );
}

const AD_URL = `${window.location.origin}/kutro-home`;
const AD_IMAGE = `${window.location.origin}/kutro-home-ad.png`;

const adCopy = {
  headline: "Why are you paying $40,000+ more than you need to?",
  body: "The 30-year mortgage hasn't changed since 1938. On a $100,000 loan at 7.5%, you pay $251,717. Kojiru Home's Flat Fee Home Loan costs $211,285 — saving you $40,000+. The savings scale with every dollar you borrow. Calculate your savings now.",
  hashtags: "#KojirHome #FlatFeeMortgage #HomeLoans #DeFi #Fintech #RealEstate",
  short: "On a $100K mortgage, you're paying $40,000+ more than you need to. There's now a structural alternative. Calculate your savings:",
};

const socialChannels = [
  {
    name: 'Telegram',
    icon: '✈',
    color: 'bg-[#26A5E4]/10 hover:bg-[#26A5E4]/20 text-[#26A5E4]',
    getUrl: () => {
      const text = encodeURIComponent(`${adCopy.short}\n\n${adCopy.hashtags}`);
      return `https://t.me/share/url?url=${encodeURIComponent(AD_URL)}&text=${text}`;
    },
  },
  {
    name: 'Discord',
    icon: '🎮',
    color: 'bg-[#5865F2]/10 hover:bg-[#5865F2]/20 text-[#5865F2]',
    getUrl: () => `https://discord.gg/AuGUAUHk`,
  },
  {
    name: 'Farcaster',
    icon: '🟣',
    color: 'bg-purple-500/10 hover:bg-purple-500/20 text-purple-400',
    getUrl: () => {
      const text = encodeURIComponent(`${adCopy.short}\n\n${adCopy.hashtags}`);
      return `https://warpcast.com/~/compose?text=${text}&embeds[]=${encodeURIComponent(AD_URL)}`;
    },
  },
  {
    name: 'Facebook',
    icon: 'f',
    color: 'bg-blue-600/10 hover:bg-blue-600/20 text-blue-500',
    getUrl: () => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(AD_URL)}&quote=${encodeURIComponent(adCopy.short)}`,
  },
  {
    name: 'LinkedIn',
    icon: 'in',
    color: 'bg-sky-500/10 hover:bg-sky-500/20 text-sky-400',
    getUrl: () => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(AD_URL)}`,
  },
  {
    name: 'Telegram',
    icon: '✈',
    color: 'bg-cyan-500/10 hover:bg-cyan-500/20 text-cyan-400',
    getUrl: () => `https://t.me/share/url?url=${encodeURIComponent(AD_URL)}&text=${encodeURIComponent(adCopy.short)}`,
  },
  {
    name: 'Reddit',
    icon: 'r/',
    color: 'bg-orange-500/10 hover:bg-orange-500/20 text-orange-400',
    getUrl: () => `https://www.reddit.com/submit?url=${encodeURIComponent(AD_URL)}&title=${encodeURIComponent(adCopy.headline)}`,
  },
];

function BDLeadsTab() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API}/api/bd/leads?limit=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const body = await res.json();
        if (!res.ok) throw new Error(body?.detail || 'Failed to load leads');
        if (alive) setData(body);
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const leads = data?.leads || [];
  const q = filter.trim().toLowerCase();
  const filtered = q
    ? leads.filter((l) =>
        [l.email, l.name, l.company, l.role, l.intent, l.source]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      )
    : leads;

  const totalDownloads = leads.reduce((s, l) => s + (l.download_count || 0), 0);
  const uniqueCompanies = new Set(leads.map((l) => (l.company || '').trim().toLowerCase()).filter(Boolean)).size;
  const downloaded = leads.filter((l) => (l.download_count || 0) > 0).length;

  const exportCsv = () => {
    const rows = [
      ['created_at', 'asset', 'name', 'email', 'company', 'role', 'intent', 'source', 'download_count', 'last_downloaded_at'],
      ...filtered.map((l) => [
        l.created_at, l.asset, l.name, l.email,
        l.company || '', l.role || '', l.intent || '', l.source || '',
        l.download_count || 0, l.last_downloaded_at || '',
      ]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bd-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="bd-leads-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Total Leads" value={leads.length} icon={Users} color="text-blue-400" delay={0} />
        <StatCard label="Downloaded" value={downloaded} icon={Download} color="text-green-400" delay={0.05} />
        <StatCard label="Total Downloads" value={totalDownloads} icon={Activity} color="text-orange-400" delay={0.1} />
        <StatCard label="Unique Companies" value={uniqueCompanies} icon={Layers} color="text-purple-400" delay={0.15} />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 w-full sm:max-w-sm">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="flex-1 px-3.5 py-2 bg-white/[0.04] border border-white/10 rounded-lg text-sm focus:outline-none focus:border-blue-500/70 placeholder:text-gray-600"
            placeholder="Filter by email, name, company, intent…"
            data-testid="bd-leads-filter"
          />
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/bd/checkpoints"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-blue-400 hover:underline inline-flex items-center gap-1"
            data-testid="bd-leads-public-link"
          >
            Open public gate <ExternalLink className="w-3 h-3" />
          </a>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={!filtered.length}
            className="border-white/10 text-xs"
            data-testid="bd-leads-export-csv"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      <div className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden" data-testid="bd-leads-table-wrap">
        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading BD leads…</div>
        ) : error ? (
          <div className="text-center py-16 text-red-400" data-testid="bd-leads-error">{error}</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-500" data-testid="bd-leads-empty">
            No leads yet. Share <code className="text-gray-300">/bd/checkpoints</code> with a UTM tag to start tracking.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="bd-leads-table">
              <thead className="text-xs uppercase tracking-wider text-gray-500 border-b border-white/5">
                <tr>
                  <th className="text-left px-4 py-3">When</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Company · Role</th>
                  <th className="text-left px-4 py-3">Intent</th>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="text-right px-4 py-3">DLs</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {filtered.map((l) => (
                  <tr key={l.id} className="hover:bg-white/[0.02]" data-testid={`bd-lead-row-${l.id}`}>
                    <td className="px-4 py-3 text-gray-400 whitespace-nowrap">
                      {new Date(l.created_at).toLocaleString(undefined, {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td className="px-4 py-3 text-white">{l.name}</td>
                    <td className="px-4 py-3 text-blue-300">
                      <a href={`mailto:${l.email}`} className="hover:underline">{l.email}</a>
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {l.company || <span className="text-gray-600">—</span>}
                      {l.role && <span className="text-gray-500"> · {l.role}</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-400 max-w-xs">
                      {l.intent ? (
                        <span title={l.intent} className="line-clamp-2">{l.intent}</span>
                      ) : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {l.source || (l.utm?.utm_source) || <span className="text-gray-700">direct</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {(l.download_count || 0) > 0 ? (
                        <span className="inline-flex items-center gap-1 text-green-400 font-medium">
                          {l.download_count}
                        </span>
                      ) : (
                        <span className="text-gray-600">0</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}



function MarketingTab() {
  const [copied, setCopied] = React.useState(null);

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="marketing-tab">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center gap-3 mb-6">
          <Megaphone className="w-6 h-6 text-emerald-400" />
          <h2 className="text-xl font-bold">Kojiru Home — Marketing Hub</h2>
        </div>

        {/* Ad Preview Card */}
        <div className="bg-white/[0.03] border border-white/5 rounded-2xl overflow-hidden mb-6" data-testid="ad-preview">
          <div className="relative">
            <img src="/kutro-home-ad.png" alt="Kojiru Home Ad" className="w-full h-64 object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-medium mb-3">
                Kojiru Home
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">{adCopy.headline}</h3>
              <p className="text-sm text-gray-300 leading-relaxed max-w-2xl">{adCopy.body}</p>
            </div>
          </div>

          {/* Ad Copy Section */}
          <div className="p-6 space-y-4 border-t border-white/5">
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500 uppercase tracking-wider">Short Copy (X, Farcaster, Telegram)</span>
                <button
                  onClick={() => copyToClipboard(`${adCopy.short}\n\n${AD_URL}\n\n${adCopy.hashtags}`, 'short')}
                  data-testid="copy-short"
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-white transition-colors"
                >
                  {copied === 'short' ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                  {copied === 'short' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="bg-black/30 rounded-lg p-3 text-sm text-gray-300 font-mono leading-relaxed">
                {adCopy.short}<br /><br />
                <span className="text-emerald-400">{AD_URL}</span><br /><br />
                <span className="text-blue-400">{adCopy.hashtags}</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500 uppercase tracking-wider">Long Copy (LinkedIn, Facebook, Blog)</span>
                <button
                  onClick={() => copyToClipboard(`${adCopy.headline}\n\n${adCopy.body}\n\n${AD_URL}\n\n${adCopy.hashtags}`, 'long')}
                  data-testid="copy-long"
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-white transition-colors"
                >
                  {copied === 'long' ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                  {copied === 'long' ? 'Copied' : 'Copy'}
                </button>
              </div>
              <div className="bg-black/30 rounded-lg p-3 text-sm text-gray-300 font-mono leading-relaxed">
                <span className="text-white font-bold">{adCopy.headline}</span><br /><br />
                {adCopy.body}<br /><br />
                <span className="text-emerald-400">{AD_URL}</span><br /><br />
                <span className="text-blue-400">{adCopy.hashtags}</span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500 uppercase tracking-wider">Link & Image</span>
                <button
                  onClick={() => copyToClipboard(AD_URL, 'link')}
                  data-testid="copy-link"
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-white transition-colors"
                >
                  {copied === 'link' ? <CheckCircle2 className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                  {copied === 'link' ? 'Copied' : 'Copy Link'}
                </button>
              </div>
              <div className="bg-black/30 rounded-lg p-3 text-sm font-mono space-y-1">
                <div><span className="text-gray-500">Page:</span> <span className="text-emerald-400">{AD_URL}</span></div>
                <div><span className="text-gray-500">Image:</span> <span className="text-emerald-400">{AD_IMAGE}</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Share Buttons */}
        <div className="bg-white/[0.03] border border-white/5 rounded-2xl p-6" data-testid="social-share-panel">
          <div className="flex items-center gap-2 mb-4">
            <Share2 className="w-5 h-5 text-emerald-400" />
            <h3 className="font-semibold">Share to Social Media</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {socialChannels.map((ch) => (
              <a
                key={ch.name}
                href={ch.getUrl()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`share-${ch.name.toLowerCase().replace(/[\s()]/g, '-')}`}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl border border-white/5 transition-all ${ch.color}`}
              >
                <span className="text-lg font-bold w-7 text-center">{ch.icon}</span>
                <div>
                  <div className="text-sm font-medium">{ch.name}</div>
                  <div className="text-[10px] text-gray-500 flex items-center gap-1">
                    Post now <ExternalLink className="w-2.5 h-2.5" />
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}

const HOME_STATUS_CFG = {
  submitted:              { label: 'Submitted',    color: 'text-blue-400',   bg: 'bg-blue-500/10',   border: 'border-blue-500/20' },
  under_review:           { label: 'Under Review',  color: 'text-amber-400',  bg: 'bg-amber-500/10',  border: 'border-amber-500/20' },
  approved:               { label: 'Approved',      color: 'text-emerald-400',bg: 'bg-emerald-500/10',border: 'border-emerald-500/20' },
  conditionally_approved: { label: 'Cond. Approved',color: 'text-cyan-400',   bg: 'bg-cyan-500/10',   border: 'border-cyan-500/20' },
  funded:                 { label: 'Funded',         color: 'text-green-400',  bg: 'bg-green-500/10',  border: 'border-green-500/20' },
  declined:               { label: 'Declined',      color: 'text-red-400',    bg: 'bg-red-500/10',    border: 'border-red-500/20' },
  closed:                 { label: 'Closed',        color: 'text-gray-400',   bg: 'bg-gray-500/10',   border: 'border-gray-500/20' },
};
const ALL_STATUSES = ['under_review', 'approved', 'conditionally_approved', 'funded', 'declined', 'closed'];
const TIER_COLORS = { A: '#10b981', B: '#3b82f6', C: '#f59e0b' };
const STATUS_CHART_COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#06b6d4', '#22c55e', '#ef4444', '#6b7280'];
const fmtUsd = (n) => Number(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

function HomeLoansTab() {
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchData = async () => {
    try {
      const res = await fetch(`${API}/api/home/admin/analytics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setData(await res.json());
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleStatusChange = async (appId, newStatus) => {
    setUpdating(appId);
    try {
      const res = await fetch(`${API}/api/home/applications/${appId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus, notes: `Status changed to ${newStatus} by admin` }),
      });
      if (res.ok) await fetchData();
      else alert((await res.json()).detail || 'Update failed');
    } catch { alert('Network error'); }
    setUpdating(null);
  };

  if (loading) return <div className="text-center py-20 text-gray-500">Loading Kojiru Home analytics...</div>;
  if (!data) return <div className="text-center py-20 text-red-400">Failed to load analytics.</div>;

  const statusChartData = Object.entries(data.status_distribution)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({ name: HOME_STATUS_CFG[k]?.label || k, value: v }));

  const tierChartData = Object.entries(data.tier_distribution).map(([k, v]) => ({ name: `Grade ${k}`, value: v }));

  const filteredApps = statusFilter === 'all'
    ? data.applications
    : data.applications.filter(a => a.status === statusFilter);

  return (
    <div className="space-y-6" data-testid="home-loans-tab">
      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        <StatCard label="Applications" value={data.total_applications} icon={FileText} color="text-blue-400" delay={0} />
        <StatCard label="Approval Rate" value={`${data.approval_rate}%`} icon={CheckCircle2} color="text-emerald-400" delay={0.05} />
        <StatCard label="Avg Savings" value={fmtUsd(data.avg_savings)} icon={TrendingUp} color="text-cyan-400" delay={0.1} />
        <StatCard label="Total Volume" value={fmtUsd(data.total_volume)} icon={DollarSign} color="text-amber-400" delay={0.15} />
        <StatCard label="Rainy Day Fund" value={fmtUsd(data.rainy_day_balance)} icon={PiggyBank} color="text-pink-400" delay={0.2} />
        <StatCard label="Pre-Qualifications" value={data.total_prequalifications} icon={UserCheck} color="text-violet-400" delay={0.25} />
      </div>

      {/* Avg Metrics Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
          <div className="text-[10px] text-gray-500 uppercase">Avg Loan</div>
          <div className="text-lg font-bold text-white">{fmtUsd(data.avg_loan_amount)}</div>
        </div>
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
          <div className="text-[10px] text-gray-500 uppercase">Avg DTI</div>
          <div className={`text-lg font-bold ${data.avg_dti <= 36 ? 'text-emerald-400' : 'text-amber-400'}`}>{data.avg_dti}%</div>
        </div>
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
          <div className="text-[10px] text-gray-500 uppercase">Avg LTV</div>
          <div className={`text-lg font-bold ${data.avg_ltv <= 80 ? 'text-emerald-400' : 'text-amber-400'}`}>{data.avg_ltv}%</div>
        </div>
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4">
          <div className="text-[10px] text-gray-500 uppercase">Total Offers</div>
          <div className="text-lg font-bold text-white">{data.total_offers}</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Status Distribution */}
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-5" data-testid="status-chart">
          <h3 className="text-sm font-semibold text-gray-400 mb-4">Status Distribution</h3>
          {statusChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={statusChartData} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={3}>
                  {statusChartData.map((_, i) => <Cell key={i} fill={STATUS_CHART_COLORS[i % STATUS_CHART_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8, fontSize: 12 }} />
                <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-gray-600 text-center py-10">No applications yet</div>}
        </div>

        {/* Tier Distribution */}
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-5" data-testid="tier-chart">
          <h3 className="text-sm font-semibold text-gray-400 mb-4">Credit Tier Distribution</h3>
          {tierChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={tierChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                <XAxis dataKey="name" tick={{ fill: '#888', fontSize: 12 }} axisLine={false} />
                <YAxis tick={{ fill: '#888', fontSize: 12 }} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: '#111', border: '1px solid #333', borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {tierChartData.map((entry, i) => (
                    <Cell key={i} fill={TIER_COLORS[entry.name.replace('Grade ', '')] || '#6b7280'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-gray-600 text-center py-10">No applications yet</div>}
        </div>
      </div>

      {/* Applications Table */}
      <div className="bg-white/[0.02] border border-white/5 rounded-xl overflow-hidden" data-testid="applications-table">
        <div className="flex items-center justify-between p-5 border-b border-white/5">
          <h3 className="text-sm font-semibold text-gray-400">All Applications ({filteredApps.length})</h3>
          <div className="flex gap-1.5 flex-wrap">
            <button
              onClick={() => setStatusFilter('all')}
              className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${statusFilter === 'all' ? 'bg-white/10 text-white' : 'text-gray-500 hover:text-white'}`}
              data-testid="filter-all"
            >All</button>
            {Object.entries(HOME_STATUS_CFG).map(([k, v]) => (
              <button
                key={k}
                onClick={() => setStatusFilter(k)}
                className={`px-2.5 py-1 rounded-md text-[10px] font-semibold transition-colors ${statusFilter === k ? `${v.bg} ${v.color}` : 'text-gray-500 hover:text-white'}`}
                data-testid={`filter-${k}`}
              >{v.label}</button>
            ))}
          </div>
        </div>

        {filteredApps.length === 0 ? (
          <div className="p-10 text-center text-gray-600 text-sm">No applications match this filter.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {filteredApps.map(app => {
              const cfg = HOME_STATUS_CFG[app.status] || HOME_STATUS_CFG.submitted;
              const isExpanded = expanded === app.application_id;
              return (
                <div key={app.application_id}>
                  <button
                    onClick={() => setExpanded(isExpanded ? null : app.application_id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-white/[0.02] transition-colors text-left"
                    data-testid={`admin-app-row-${app.application_id}`}
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="shrink-0">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold ${cfg.bg} ${cfg.border} ${cfg.color} border`}>
                          {cfg.label}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-mono text-white">{app.application_id}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {app.applicant_email || 'Unknown'} &middot; {fmtUsd(app.loan_amount)} &middot; Tier {app.credit_tier?.grade || '?'}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-gray-600 hidden sm:block">{new Date(app.created_at).toLocaleDateString()}</span>
                      {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
                    </div>
                  </button>

                  {isExpanded && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="px-5 pb-5 space-y-4 border-t border-white/5 bg-white/[0.01]">
                      {/* Metrics */}
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 pt-3">
                        <div className="bg-white/[0.03] rounded-lg p-2.5">
                          <div className="text-[10px] text-gray-500">Tier</div>
                          <div className="text-sm font-bold text-emerald-400">{app.credit_tier?.grade || '—'}</div>
                        </div>
                        <div className="bg-white/[0.03] rounded-lg p-2.5">
                          <div className="text-[10px] text-gray-500">DTI</div>
                          <div className={`text-sm font-bold ${app.dti_ratio <= 36 ? 'text-emerald-400' : 'text-amber-400'}`}>{app.dti_ratio}%</div>
                        </div>
                        <div className="bg-white/[0.03] rounded-lg p-2.5">
                          <div className="text-[10px] text-gray-500">LTV</div>
                          <div className={`text-sm font-bold ${app.ltv_ratio <= 80 ? 'text-emerald-400' : 'text-amber-400'}`}>{app.ltv_ratio}%</div>
                        </div>
                        <div className="bg-white/[0.03] rounded-lg p-2.5">
                          <div className="text-[10px] text-gray-500">Savings</div>
                          <div className="text-sm font-bold text-cyan-400">{app.comparison ? fmtUsd(app.comparison.savings) : '—'}</div>
                        </div>
                        <div className="bg-white/[0.03] rounded-lg p-2.5">
                          <div className="text-[10px] text-gray-500">Offers</div>
                          <div className="text-sm font-bold text-white">{app.offers?.length || 0}</div>
                        </div>
                      </div>

                      {/* Property */}
                      <div className="text-xs text-gray-400">
                        <span className="text-gray-600">Property:</span> {app.property_address || 'N/A'} ({app.property_type?.replace('_', ' ')})
                      </div>

                      {/* Timeline */}
                      {app.status_history?.length > 0 && (
                        <div>
                          <div className="text-[10px] text-gray-500 uppercase mb-1.5">Timeline</div>
                          <div className="space-y-1">
                            {app.status_history.map((h, i) => (
                              <div key={i} className="flex items-center gap-2 text-xs">
                                <div className="w-1 h-1 rounded-full bg-emerald-400 shrink-0" />
                                <span className="text-white">{HOME_STATUS_CFG[h.status]?.label || h.status}</span>
                                {h.notes && <span className="text-gray-600">— {h.notes}</span>}
                                <span className="text-gray-700 ml-auto shrink-0">{new Date(h.timestamp).toLocaleString()}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Status Change */}
                      <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                        <span className="text-[10px] text-gray-500 uppercase shrink-0">Change Status:</span>
                        <div className="flex gap-1.5 flex-wrap">
                          {ALL_STATUSES.map(s => {
                            const sc = HOME_STATUS_CFG[s];
                            const isCurrent = app.status === s;
                            return (
                              <button
                                key={s}
                                onClick={() => !isCurrent && handleStatusChange(app.application_id, s)}
                                disabled={isCurrent || updating === app.application_id}
                                data-testid={`set-status-${s}-${app.application_id}`}
                                className={`px-2.5 py-1 rounded-md text-[10px] font-semibold border transition-colors ${
                                  isCurrent
                                    ? `${sc.bg} ${sc.border} ${sc.color} cursor-default`
                                    : `border-white/5 text-gray-500 hover:${sc.color} hover:${sc.bg} disabled:opacity-30`
                                }`}
                              >
                                {updating === app.application_id ? '...' : sc.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Copyright */}
      <div className="text-center pt-4">
        <p className="text-[10px] text-gray-600" data-testid="admin-copyright-notice">
          &copy; 2026 Kojiru, Inc. All Rights Reserved. The Kojiru Home Flat Fee Home Loan concept, structure, and methodology are proprietary and protected under applicable intellectual property laws.
        </p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, token, login, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [roleEdit, setRoleEdit] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [securityStats, setSecurityStats] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [flaggedUsers, setFlaggedUsers] = useState([]);
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwLoading, setPwLoading] = useState(false);
  const [pwMessage, setPwMessage] = useState(null);
  const [acpStats, setAcpStats] = useState(null);
  const [aaasStats, setAaasStats] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  const isAdmin = user?.role === 'admin';

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const userData = await login(adminEmail, adminPassword);
      if (userData.role !== 'admin') {
        setLoginError('This account does not have admin privileges.');
      }
    } catch (err) {
      setLoginError(err.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoginLoading(false);
    }
  };

  const promoteToAdmin = async () => {
    setPromoting(true);
    try {
      const res = await fetch(`${API}/api/admin/promote`, { method: 'POST', headers, body: '{}' });
      if (res.ok) {
        window.location.reload();
      } else {
        const d = await res.json();
        alert(d.detail || 'Failed to promote');
      }
    } catch (e) { alert('Failed to promote: ' + (e.message || 'Unknown error')); } finally { setPromoting(false); }
  };

  useEffect(() => {
    if (!isAdmin) return;
    Promise.all([
      fetch(`${API}/api/admin/stats`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API}/api/admin/users`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API}/api/admin/analytics`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/admin/ai-security-stats`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/admin/ai-audit-log?limit=50`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/admin/flagged-users`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/agent-credit/stats`).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/aaas/stats`).then(r => r.json()).catch(() => null),
    ]).then(([s, u, a, sec, log, flagged, acp, aaas]) => {
      setStats(s);
      setUsers(u.users || []);
      setAnalytics(a);
      setSecurityStats(sec);
      setAuditLog(log?.events || []);
      setFlaggedUsers(flagged?.users || []);
      setAcpStats(acp);
      setAaasStats(aaas);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [isAdmin, token]);

  useEffect(() => {
    if (!isAdmin) return;
    Promise.all([
      fetch(`${API}/api/guardian/status`).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/guardian/contracts`).then(r => r.json()).catch(() => null),
      fetch(`${API}/api/guardian/playbook`).then(r => r.json()).catch(() => null),
    ]).then(([status, contracts, playbook]) => {
      setGuardianData(status);
      setGuardianContracts(contracts?.contracts || []);
      setGuardianPlaybook(playbook);
    });
    fetch(`${API}/api/sentinel/status`).then(r => r.json()).then(setSentinelData).catch(() => {});
  }, [isAdmin]);

  const updateRole = async (userId, newRole) => {
    await fetch(`${API}/api/admin/users/${userId}/role`, {
      method: 'PUT', headers, body: JSON.stringify({ role: newRole })
    });
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
    setRoleEdit(null);
  };

  const deleteUser = async (userId, name) => {
    if (!window.confirm(`Delete user "${name}"? This cannot be undone.`)) return;
    await fetch(`${API}/api/admin/users/${userId}`, { method: 'DELETE', headers });
    setUsers(prev => prev.filter(u => u.id !== userId));
  };

  const unflagUser = async (userId) => {
    await fetch(`${API}/api/admin/users/${userId}/unflag`, { method: 'PUT', headers });
    setFlaggedUsers(prev => prev.filter(u => u.id !== userId));
  };

  const flagUser = async (userId, name) => {
    if (!window.confirm(`Flag "${name}"? This will suspend their AI access.`)) return;
    await fetch(`${API}/api/admin/users/${userId}/flag`, {
      method: 'PUT', headers, body: JSON.stringify({ reason: 'Manually flagged by admin' })
    });
    setFlaggedUsers(prev => [...prev, { id: userId, name, ai_flagged_reason: 'Manually flagged by admin', ai_flagged_at: new Date().toISOString() }]);
  };

  const [digestSending, setDigestSending] = useState(false);
  const [guardianData, setGuardianData] = useState(null);
  const [guardianContracts, setGuardianContracts] = useState([]);
  const [guardianPlaybook, setGuardianPlaybook] = useState(null);
  const [sentinelData, setSentinelData] = useState(null);
  const sendDigest = async () => {
    setDigestSending(true);
    try {
      const res = await fetch(`${API}/api/admin/security-digest/send`, { method: 'POST', headers });
      const data = await res.json();
      alert(res.ok ? `Digest sent! (${data.total_events} events)` : `Failed: ${data.detail}`);
    } catch { alert('Failed to send digest'); }
    setDigestSending(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPwMessage(null);
    if (newPw !== confirmPw) {
      setPwMessage({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    if (newPw.length < 6) {
      setPwMessage({ type: 'error', text: 'New password must be at least 6 characters.' });
      return;
    }
    setPwLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/change-password`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      const data = await res.json();
      if (res.ok) {
        setPwMessage({ type: 'success', text: 'Password changed successfully.' });
        setCurrentPw('');
        setNewPw('');
        setConfirmPw('');
      } else {
        setPwMessage({ type: 'error', text: data.detail || 'Failed to change password.' });
      }
    } catch {
      setPwMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setPwLoading(false);
    }
  };

  const fmt = (n) => {
    if (!n && n !== 0) return '--';
    if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
    return typeof n === 'number' && n % 1 !== 0 ? `$${n.toFixed(2)}` : String(n);
  };

  const fmtNum = (n) => {
    if (!n && n !== 0) return '--';
    if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
    if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
    return String(Math.round(n));
  };

  const exportCSV = (data, filename, columns) => {
    const header = columns.map(c => c.label).join(',');
    const rows = data.map(row => columns.map(c => {
      const val = row[c.key];
      return typeof val === 'string' && val.includes(',') ? `"${val}"` : val ?? '';
    }).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${filename}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = async (title, data, columns) => {
    const { default: jsPDF } = await import('jspdf');
    await import('jspdf-autotable');
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text(`KOJIRU Admin - ${title}`, 14, 20);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);
    doc.autoTable({
      startY: 35,
      head: [columns.map(c => c.label)],
      body: data.map(row => columns.map(c => row[c.key] ?? '--')),
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });
    doc.save(`kutro_${title.toLowerCase().replace(/\s/g, '_')}.pdf`);
  };

  const exportUsers = (format) => {
    const cols = [
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'role', label: 'Role' },
      { key: 'wallet_address', label: 'Wallet' },
      { key: 'created_at', label: 'Joined' },
    ];
    if (format === 'csv') exportCSV(users, 'kutro_users', cols);
    else exportPDF('User Report', users, cols);
  };

  const exportLoans = (format) => {
    const cols = [
      { key: 'date', label: 'Date' },
      { key: 'count', label: 'Requests' },
      { key: 'volume', label: 'Volume ($)' },
    ];
    const data = analytics?.loan_activity || [];
    if (format === 'csv') exportCSV(data, 'kutro_loan_activity', cols);
    else exportPDF('Loan Activity', data, cols);
  };

  const exportAnalyticsSummary = (format) => {
    const data = [
      { metric: 'Total Users', value: stats?.users?.total || 0 },
      { metric: 'Lenders', value: stats?.users?.lenders || 0 },
      { metric: 'Admins', value: stats?.users?.admins || 0 },
      { metric: 'Registered Agents', value: acpStats?.total_registered_agents || 0 },
      { metric: 'Active Credit Lines', value: acpStats?.active_credit_lines || 0 },
      { metric: 'Total Credit Issued ($)', value: acpStats?.total_credit_issued || 0 },
      { metric: 'USDC Collateral Staked (in profiles)', value: acpStats?.total_kutro_staked || 0 },
      { metric: 'Agent Revenue ($)', value: acpStats?.total_agent_revenue || 0 },
      { metric: 'Active Subscriptions', value: aaasStats?.active_subscriptions || 0 },
      { metric: 'SOPs Published', value: aaasStats?.sops_published || 0 },
      { metric: 'Memory Packs', value: aaasStats?.memory_packs || 0 },
      { metric: 'Certified Agents', value: aaasStats?.certified_agents || 0 },
      { metric: 'Marketplace Revenue (KOJI)', value: aaasStats?.marketplace_revenue_kojiru || 0 },
      { metric: 'Proposals', value: stats?.governance?.total_proposals || 0 },
      { metric: 'Notifications Sent', value: stats?.notifications?.total || 0 },
    ];
    const cols = [{ key: 'metric', label: 'Metric' }, { key: 'value', label: 'Value' }];
    if (format === 'csv') exportCSV(data, 'kojiru_platform_summary', cols);
    else exportPDF('Platform Summary', data, cols);
  };

  // Non-admin gate
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#050505]">
        <Navbar />
        <div className="pt-32 pb-20 px-6">
          <div className="max-w-sm mx-auto">
            <div className="text-center mb-8">
              <Shield className="w-14 h-14 text-blue-500/60 mx-auto mb-4" />
              <h1 className="text-2xl font-bold mb-2">Admin Portal</h1>
              <p className="text-sm text-gray-500">Sign in with your admin credentials</p>
            </div>

            {/* Inline Login Form */}
            <form onSubmit={handleAdminLogin} className="space-y-4" data-testid="admin-login-form">
              {loginError && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="admin-login-error">
                  {loginError}
                </div>
              )}
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Email</label>
                <input
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="admin@kojiru.com"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500/50 transition-colors"
                  required
                  data-testid="admin-email-input"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 mb-1.5 block">Password</label>
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500/50 transition-colors"
                  required
                  data-testid="admin-password-input"
                />
              </div>
              <Button
                type="submit"
                disabled={loginLoading}
                className="w-full h-11 bg-gradient-to-r from-blue-500 to-purple-500 hover:opacity-90"
                data-testid="admin-login-btn"
              >
                {loginLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  <><Lock className="w-4 h-4 mr-2" /> Sign In</>
                )}
              </Button>
            </form>

            {user && user.role !== 'admin' && (
              <p className="text-xs text-gray-600 text-center mt-4">
                Signed in as <span className="text-gray-400">{user.email}</span> — not an admin account.
              </p>
            )}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      <div className="pt-28 pb-20 px-6">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Shield className="w-6 h-6 text-blue-400" />
                  <h1 className="text-3xl font-bold" data-testid="admin-heading">Admin Dashboard</h1>
                </div>
                <p className="text-gray-400">Platform overview and user management</p>
              </div>
              <Button
                variant="outline"
                onClick={() => { logout(); navigate('/'); }}
                className="border-white/10 text-gray-400 hover:text-white hover:bg-white/5"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
              </Button>
            </div>
          </motion.div>

          {/* Tabs */}
          <div className="flex gap-2 mb-8 border-b border-white/5 pb-3 overflow-x-auto whitespace-nowrap" data-testid="admin-tab-bar">
            {['overview', 'analytics', 'users', 'activity', 'security', 'guardian', 'marketing', 'bd-leads', 'claim-comments', 'pilot-signups', 'settings'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'text-gray-500 hover:text-white hover:bg-white/5'}`}
                data-testid={`tab-${tab}`}
              >
                {tab === 'home-loans' ? 'Home Loans' : tab === 'guardian' ? 'Guardian' : tab === 'bd-leads' ? 'BD Leads' : tab === 'claim-comments' ? 'Claim Comments' : tab === 'pilot-signups' ? 'Pilot Signups' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-20 text-gray-500">Loading dashboard data...</div>
          ) : activeTab === 'overview' ? (
            /* Overview Tab */
            <div className="space-y-6" data-testid="overview-tab">
              {/* Export Buttons */}
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => exportAnalyticsSummary('csv')} className="border-white/10 text-xs" data-testid="export-summary-csv">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportAnalyticsSummary('pdf')} className="border-white/10 text-xs" data-testid="export-summary-pdf">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Export PDF
                </Button>
              </div>
              <FeeRevenuePanel />
              <ClaimDecisionPanel />
              <BreachRecordAdminPanel />
              {/* Stats Grid - Users */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Users className="w-4 h-4 text-blue-400" />
                  <h3 className="text-sm font-semibold text-gray-300">User Base</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard label="Total Users" value={stats?.users?.total || 0} icon={Users} color="text-blue-400" delay={0} />
                  <StatCard label="Registered Agents" value={fmtNum(acpStats?.total_registered_agents || 0)} icon={Bot} color="text-cyan-400" sub="on-chain" delay={0.05} />
                  <StatCard label="Lenders" value={stats?.users?.lenders || 0} icon={TrendingUp} color="text-purple-400" sub="capital providers" delay={0.1} />
                  <StatCard label="Admins" value={stats?.users?.admins || 0} icon={Shield} color="text-orange-400" delay={0.15} />
                </div>
              </div>

              {/* Stats Grid - Agent Credit Protocol */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="w-4 h-4 text-emerald-400" />
                  <h3 className="text-sm font-semibold text-gray-300">Agent Credit Protocol</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard label="Active Credit Lines" value={fmtNum(acpStats?.active_credit_lines || 0)} icon={Layers} color="text-emerald-400" sub="bilateral" delay={0.2} />
                  <StatCard label="Credit Issued" value={fmt(acpStats?.total_credit_issued || 0)} icon={DollarSign} color="text-green-400" sub="cumulative" delay={0.25} />
                  <StatCard label="USDC Collateral Staked" value={fmtNum(acpStats?.total_kutro_staked || 0)} icon={Coins} color="text-yellow-400" sub="in profiles" delay={0.3} />
                  <StatCard label="Agent Revenue" value={fmt(acpStats?.total_agent_revenue || 0)} icon={TrendingUp} color="text-pink-400" sub="generated" delay={0.35} />
                </div>
              </div>

              {/* Stats Grid - AaaS & System */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Package className="w-4 h-4 text-violet-400" />
                  <h3 className="text-sm font-semibold text-gray-300">AaaS & System</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard label="Active Subscriptions" value={fmtNum(aaasStats?.active_subscriptions || 0)} icon={CheckCircle2} color="text-violet-400" sub="Pro / Enterprise" delay={0.4} />
                  <StatCard label="Memory Packs" value={fmtNum(aaasStats?.memory_packs || 0)} icon={Package} color="text-indigo-400" sub={`${aaasStats?.sops_published || 0} SOPs`} delay={0.45} />
                  <StatCard label="Notifications" value={stats?.notifications?.total || 0} icon={Bell} color="text-orange-400" sub="sent total" delay={0.5} />
                  <StatCard label="System Status" value="Healthy" icon={Activity} color="text-green-400" delay={0.55} />
                </div>
              </div>
            </div>
          ) : activeTab === 'analytics' ? (
            /* Analytics Tab */
            <div className="space-y-6" data-testid="analytics-tab">
              {/* Export Buttons */}
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => exportLoans('csv')} className="border-white/10 text-xs" data-testid="export-loans-csv">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Loans CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportUsers('csv')} className="border-white/10 text-xs" data-testid="export-users-csv">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Users CSV
                </Button>
                <Button variant="outline" size="sm" onClick={() => exportUsers('pdf')} className="border-white/10 text-xs" data-testid="export-users-pdf">
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Users PDF
                </Button>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                {/* User Growth Line Chart */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white/[0.03] border border-white/5 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-400" /> User Growth
                  </h3>
                  <div className="h-64" data-testid="chart-user-growth">
                    {analytics?.user_growth?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={analytics.user_growth}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#525252' }} tickFormatter={d => d.slice(5)} />
                          <YAxis tick={{ fontSize: 11, fill: '#525252' }} />
                          <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #222', borderRadius: 8, fontSize: 12 }} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Line type="monotone" dataKey="cumulative" name="Total Users" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6', r: 3 }} />
                          <Line type="monotone" dataKey="total" name="New/Day" stroke="#22c55e" strokeWidth={2} dot={{ fill: '#22c55e', r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-sm text-gray-600">No user data yet</div>
                    )}
                  </div>
                </motion.div>

                {/* Loan Volume Bar Chart */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/[0.03] border border-white/5 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-400" /> Loan Request Volume
                  </h3>
                  <div className="h-64" data-testid="chart-loan-volume">
                    {analytics?.loan_activity?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.loan_activity}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#525252' }} tickFormatter={d => d.slice(5)} />
                          <YAxis tick={{ fontSize: 11, fill: '#525252' }} />
                          <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #222', borderRadius: 8, fontSize: 12 }} formatter={(v, name) => [name === 'volume' ? `$${v.toLocaleString()}` : v, name === 'volume' ? 'Volume' : 'Requests']} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Bar dataKey="count" name="Requests" fill="#22c55e" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="volume" name="Volume ($)" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-sm text-gray-600">No loan data yet</div>
                    )}
                  </div>
                </motion.div>

                {/* Bid Activity Area Chart */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white/[0.03] border border-white/5 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-purple-400" /> Bid Activity
                  </h3>
                  <div className="h-64" data-testid="chart-bid-activity">
                    {analytics?.bid_activity?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={analytics.bid_activity}>
                          <defs>
                            <linearGradient id="bidGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                              <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                          <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#525252' }} tickFormatter={d => d.slice(5)} />
                          <YAxis tick={{ fontSize: 11, fill: '#525252' }} />
                          <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #222', borderRadius: 8, fontSize: 12 }} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                          <Area type="monotone" dataKey="count" name="Bids" stroke="#8b5cf6" fill="url(#bidGrad)" strokeWidth={2} />
                          <Line type="monotone" dataKey="avg_rate" name="Avg APR %" stroke="#f97316" strokeWidth={2} dot={{ fill: '#f97316', r: 3 }} />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-full text-sm text-gray-600">No bid data yet</div>
                    )}
                  </div>
                </motion.div>

                {/* Role Distribution Pie Chart */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white/[0.03] border border-white/5 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-orange-400" /> User Role Distribution
                  </h3>
                  <div className="h-64 flex items-center justify-center" data-testid="chart-role-dist">
                    {analytics?.role_distribution?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={analytics.role_distribution}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={90}
                            paddingAngle={4}
                            dataKey="value"
                            label={({ name, value }) => `${name}: ${value}`}
                          >
                            {analytics.role_distribution.map((entry, i) => (
                              <Cell key={i} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #222', borderRadius: 8, fontSize: 12 }} />
                          <Legend wrapperStyle={{ fontSize: 12 }} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="text-sm text-gray-600">No user data yet</div>
                    )}
                  </div>
                </motion.div>
              </div>

              {/* Governance Votes Chart */}
              {analytics?.proposal_votes?.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="bg-white/[0.03] border border-white/5 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <Vote className="w-4 h-4 text-cyan-400" /> Governance Proposal Votes
                  </h3>
                  <div className="h-56" data-testid="chart-governance">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analytics.proposal_votes} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                        <XAxis type="number" tick={{ fontSize: 11, fill: '#525252' }} />
                        <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: '#525252' }} width={120} />
                        <Tooltip contentStyle={{ background: '#0a0a0a', border: '1px solid #222', borderRadius: 8, fontSize: 12 }} />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Bar dataKey="for" name="Votes For" fill="#22c55e" radius={[0, 4, 4, 0]} />
                        <Bar dataKey="against" name="Votes Against" fill="#ef4444" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </motion.div>
              )}
            </div>
          ) : activeTab === 'users' ? (
            /* Users Tab */
            <div data-testid="users-tab">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">All Users ({users.length})</h2>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => exportUsers('csv')} className="border-white/10 text-xs" data-testid="export-users-tab-csv">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => exportUsers('pdf')} className="border-white/10 text-xs" data-testid="export-users-tab-pdf">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> PDF
                  </Button>
                </div>
              </div>
              <div className="bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="users-table">
                    <thead>
                      <tr className="border-b border-white/5 text-gray-500 text-left">
                        <th className="px-4 py-3 font-medium">Name</th>
                        <th className="px-4 py-3 font-medium">Email</th>
                        <th className="px-4 py-3 font-medium">Role</th>
                        <th className="px-4 py-3 font-medium">Wallet</th>
                        <th className="px-4 py-3 font-medium">Joined</th>
                        <th className="px-4 py-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((u) => (
                        <tr key={u.id} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors" data-testid={`user-row-${u.id}`}>
                          <td className="px-4 py-3 text-white font-medium">{u.name}</td>
                          <td className="px-4 py-3 text-gray-400">{u.email}</td>
                          <td className="px-4 py-3">
                            {roleEdit === u.id ? (
                              <div className="flex items-center gap-1">
                                {['consumer', 'lender', 'admin'].map(r => (
                                  <button
                                    key={r}
                                    onClick={() => updateRole(u.id, r)}
                                    className={`px-2 py-0.5 rounded text-xs ${u.role === r ? 'bg-blue-500 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'}`}
                                  >
                                    {r}
                                  </button>
                                ))}
                                <button onClick={() => setRoleEdit(null)} className="text-xs text-gray-500 ml-1">x</button>
                              </div>
                            ) : (
                              <span
                                onClick={() => setRoleEdit(u.id)}
                                className={`inline-block px-2.5 py-0.5 rounded-full text-xs cursor-pointer ${
                                  u.role === 'admin' ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20' :
                                  u.role === 'lender' ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' :
                                  'bg-green-500/10 text-green-400 border border-green-500/20'
                                }`}
                              >
                                {u.role}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {u.wallet_address ? (
                              <code className="text-xs text-gray-500 font-mono">{u.wallet_address.slice(0, 6)}...{u.wallet_address.slice(-4)}</code>
                            ) : (
                              <span className="text-xs text-gray-600">--</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-500">
                            {u.created_at ? new Date(u.created_at).toLocaleDateString() : '--'}
                          </td>
                          <td className="px-4 py-3">
                            {u.id !== user.id && (
                              <button
                                onClick={() => deleteUser(u.id, u.name)}
                                className="p-1.5 hover:bg-red-500/10 rounded-lg transition-colors"
                                data-testid={`delete-user-${u.id}`}
                              >
                                <Trash2 className="w-3.5 h-3.5 text-red-400" />
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : activeTab === 'activity' ? (
            /* Activity Tab */
            <div className="grid md:grid-cols-3 gap-5" data-testid="activity-tab">
              {/* Recent Users */}
              <div className="bg-white/[0.03] border border-white/5 rounded-2xl overflow-hidden">
                <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-400" />
                  <h3 className="text-sm font-semibold">Recent Users</h3>
                </div>
                <div className="divide-y divide-white/[0.03]">
                  {(stats?.recent?.users || []).map((u, i) => (
                    <div key={i} className="px-4 py-3 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium">{u.name}</div>
                        <div className="text-xs text-gray-500">{u.email}</div>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${u.role === 'lender' ? 'bg-purple-500/10 text-purple-400' : 'bg-green-500/10 text-green-400'}`}>
                        {u.role}
                      </span>
                    </div>
                  ))}
                  {(!stats?.recent?.users || stats.recent.users.length === 0) && (
                    <div className="px-4 py-8 text-center text-sm text-gray-600">No recent users</div>
                  )}
                </div>
              </div>

              {/* Recent Loan Requests */}
              <div className="bg-white/[0.03] border border-white/5 rounded-2xl overflow-hidden">
                <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-cyan-400" />
                  <h3 className="text-sm font-semibold">Recent Requests</h3>
                </div>
                <div className="divide-y divide-white/[0.03]">
                  {(stats?.recent?.requests || []).map((r, i) => (
                    <div key={i} className="px-4 py-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">${r.amount?.toLocaleString()}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${r.status === 'open' ? 'bg-blue-500/10 text-blue-400' : r.status === 'funded' ? 'bg-green-500/10 text-green-400' : 'bg-gray-500/10 text-gray-400'}`}>
                          {r.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">{r.purpose} | {r.term_months}mo</div>
                    </div>
                  ))}
                  {(!stats?.recent?.requests || stats.recent.requests.length === 0) && (
                    <div className="px-4 py-8 text-center text-sm text-gray-600">No recent requests</div>
                  )}
                </div>
              </div>

              {/* Recent Bids */}
              <div className="bg-white/[0.03] border border-white/5 rounded-2xl overflow-hidden">
                <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <h3 className="text-sm font-semibold">Recent Bids</h3>
                </div>
                <div className="divide-y divide-white/[0.03]">
                  {(stats?.recent?.bids || []).map((b, i) => (
                    <div key={i} className="px-4 py-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium">{b.interest_rate}% APR</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${b.status === 'pending' ? 'bg-yellow-500/10 text-yellow-400' : b.status === 'accepted' ? 'bg-green-500/10 text-green-400' : 'bg-gray-500/10 text-gray-400'}`}>
                          {b.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-500">{b.lender_name}</div>
                    </div>
                  ))}
                  {(!stats?.recent?.bids || stats.recent.bids.length === 0) && (
                    <div className="px-4 py-8 text-center text-sm text-gray-600">No recent bids</div>
                  )}
                </div>
              </div>
            </div>
          ) : activeTab === 'security' ? (
            /* Security Tab - AI Audit Log */
            <div className="space-y-6" data-testid="security-tab">
              {/* Header with Digest Button */}
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">AI Security Monitor</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Weekly digest emails every Monday at 9:00 AM UTC</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={sendDigest}
                  disabled={digestSending}
                  className="border-purple-500/20 text-purple-400 hover:bg-purple-500/10 text-xs"
                  data-testid="send-digest-btn"
                >
                  <Download className="w-3 h-3 mr-1.5" />
                  {digestSending ? 'Sending...' : 'Send Digest Now'}
                </Button>
              </div>
              {/* Security Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-white/[0.03] border border-white/5 rounded-xl p-5">
                  <ShieldAlert className="w-5 h-5 text-red-400 mb-3" />
                  <div className="text-2xl font-bold text-white">{securityStats?.total_events || 0}</div>
                  <div className="text-xs text-gray-500 mt-1">Total Threats Detected</div>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
                  className="bg-white/[0.03] border border-white/5 rounded-xl p-5">
                  <AlertTriangle className="w-5 h-5 text-orange-400 mb-3" />
                  <div className="text-2xl font-bold text-white">{securityStats?.by_severity?.high || 0}</div>
                  <div className="text-xs text-gray-500 mt-1">High Severity</div>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                  className="bg-white/[0.03] border border-white/5 rounded-xl p-5">
                  <Shield className="w-5 h-5 text-yellow-400 mb-3" />
                  <div className="text-2xl font-bold text-white">{securityStats?.by_severity?.medium || 0}</div>
                  <div className="text-xs text-gray-500 mt-1">Medium Severity</div>
                </motion.div>
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
                  className="bg-white/[0.03] border border-white/5 rounded-xl p-5">
                  <Eye className="w-5 h-5 text-blue-400 mb-3" />
                  <div className="text-2xl font-bold text-white">{securityStats?.by_severity?.low || 0}</div>
                  <div className="text-xs text-gray-500 mt-1">Low Severity</div>
                </motion.div>
              </div>

              {/* Top Targeted Endpoints & Top Offenders */}
              <div className="grid md:grid-cols-2 gap-5">
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                  className="bg-white/[0.03] border border-white/5 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-cyan-400" /> Targeted Endpoints
                  </h3>
                  {securityStats?.by_endpoint?.length > 0 ? (
                    <div className="space-y-2">
                      {securityStats.by_endpoint.map(e => (
                        <div key={e.endpoint} className="flex items-center justify-between py-2 border-b border-white/[0.03]">
                          <code className="text-xs text-gray-400 font-mono">{e.endpoint}</code>
                          <span className="text-sm font-medium text-red-400">{e.count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-sm text-gray-600">No targeted endpoints yet</div>
                  )}
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
                  className="bg-white/[0.03] border border-white/5 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <UserX className="w-4 h-4 text-red-400" /> Top Offenders
                  </h3>
                  {securityStats?.top_offenders?.length > 0 ? (
                    <div className="space-y-2">
                      {securityStats.top_offenders.map(o => (
                        <div key={o.user_id} className="flex items-center justify-between py-2 border-b border-white/[0.03]">
                          <code className="text-xs text-gray-400 font-mono">{o.user_id.slice(0, 16)}...</code>
                          <span className="text-sm font-medium text-orange-400">{o.count} attempts</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-sm text-gray-600">No offenders detected</div>
                  )}
                </motion.div>
              </div>

              {/* Flagged Users */}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                className="bg-white/[0.02] border border-red-500/10 rounded-2xl overflow-hidden"
                data-testid="flagged-users-panel"
              >
                <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <UserX className="w-4 h-4 text-red-400" /> Flagged Users — AI Access Suspended
                  </h3>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">{flaggedUsers.length} flagged</span>
                </div>
                {flaggedUsers.length > 0 ? (
                  <div className="divide-y divide-white/[0.03]">
                    {flaggedUsers.map(u => (
                      <div key={u.id} className="px-5 py-3 flex items-center justify-between" data-testid={`flagged-user-${u.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-white">{u.name || u.email}</span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                              u.role === 'consumer' ? 'bg-green-500/10 text-green-400' :
                              u.role === 'lender' ? 'bg-purple-500/10 text-purple-400' :
                              u.role === 'business' ? 'bg-blue-500/10 text-blue-400' :
                              'bg-orange-500/10 text-orange-400'
                            }`}>{u.role}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">{u.ai_flagged_reason}</div>
                          <div className="text-[10px] text-gray-600">
                            Flagged: {u.ai_flagged_at ? new Date(u.ai_flagged_at).toLocaleString() : '--'}
                            {u.ai_threat_count ? ` | ${u.ai_threat_count} injection attempts` : ''}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => unflagUser(u.id)}
                          className="border-green-500/20 text-green-400 hover:bg-green-500/10 text-xs ml-4"
                          data-testid={`unflag-btn-${u.id}`}
                        >
                          Restore Access
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="px-5 py-10 text-center">
                    <Shield className="w-8 h-8 text-green-500/30 mx-auto mb-2" />
                    <div className="text-sm text-gray-500">No flagged users</div>
                    <div className="text-xs text-gray-600 mt-1">Users with 3+ injection attempts are auto-flagged</div>
                  </div>
                )}
              </motion.div>

              {/* Audit Log Table */}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                className="bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden">
                <div className="px-5 py-4 border-b border-white/5 flex items-center justify-between">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <ShieldAlert className="w-4 h-4 text-red-400" /> Injection Attempt Log
                  </h3>
                  <span className="text-xs text-gray-500">{auditLog.length} events</span>
                </div>
                {auditLog.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="audit-log-table">
                      <thead>
                        <tr className="border-b border-white/5 text-gray-500 text-left text-xs">
                          <th className="px-4 py-3 font-medium">Timestamp</th>
                          <th className="px-4 py-3 font-medium">Severity</th>
                          <th className="px-4 py-3 font-medium">Endpoint</th>
                          <th className="px-4 py-3 font-medium">Field</th>
                          <th className="px-4 py-3 font-medium">Patterns Matched</th>
                          <th className="px-4 py-3 font-medium">Original Input</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditLog.map((evt, i) => (
                          <tr key={i} className="border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors" data-testid={`audit-row-${i}`}>
                            <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                              {new Date(evt.timestamp).toLocaleString()}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                evt.severity === 'high' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                                evt.severity === 'medium' ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20' :
                                'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'
                              }`}>
                                {evt.severity}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <code className="text-xs text-cyan-400 font-mono">{evt.endpoint}</code>
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-400">{evt.field}</td>
                            <td className="px-4 py-3 text-xs text-gray-400">
                              {evt.patterns_matched?.map((p, j) => (
                                <span key={j} className="inline-block mr-1 mb-1 px-1.5 py-0.5 rounded bg-red-500/10 text-red-300 text-[10px]">
                                  {p.length > 30 ? p.slice(0, 30) + '...' : p}
                                </span>
                              ))}
                            </td>
                            <td className="px-4 py-3 text-xs text-gray-500 max-w-[200px] truncate" title={evt.original_snippet}>
                              {evt.original_snippet?.slice(0, 60)}{evt.original_snippet?.length > 60 ? '...' : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="px-5 py-16 text-center">
                    <Shield className="w-10 h-10 text-green-500/30 mx-auto mb-3" />
                    <div className="text-sm text-gray-500">No injection attempts detected</div>
                    <div className="text-xs text-gray-600 mt-1">All AI endpoints are being monitored</div>
                  </div>
                )}
              </motion.div>
            </div>
          ) : activeTab === 'guardian' ? (
            /* Guardian Pause — On-Chain Emergency Freeze System */
            <div className="space-y-6" data-testid="guardian-tab">
              {/* Status Banner */}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                className={`rounded-xl p-6 border ${guardianData?.protocol_paused
                  ? 'bg-red-500/10 border-red-500/30'
                  : 'bg-emerald-500/5 border-emerald-500/20'
                }`}
                data-testid="guardian-status-banner"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      guardianData?.protocol_paused ? 'bg-red-500/20' : 'bg-emerald-500/20'
                    }`}>
                      <Shield className={`w-6 h-6 ${guardianData?.protocol_paused ? 'text-red-400' : 'text-emerald-400'}`} />
                    </div>
                    <div>
                      <h2 className="text-lg font-bold text-white">
                        {guardianData?.protocol_paused ? 'PROTOCOL PAUSED' : 'Protocol Operational'}
                      </h2>
                      <p className="text-xs text-gray-400 mt-0.5">
                        Guardian Pause — On-Chain Emergency Freeze System
                      </p>
                      {guardianData?.protocol_paused && guardianData.reason && (
                        <p className="text-xs text-red-300 mt-1">Reason: {guardianData.reason}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xs px-3 py-1 rounded-full font-medium ${
                      guardianData?.protocol_paused
                        ? 'bg-red-500/20 text-red-300 border border-red-500/30'
                        : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                    }`}>
                      {guardianData?.protocol_paused ? 'PAUSED' : 'ACTIVE'}
                    </div>
                    <div className="text-[10px] text-gray-600 mt-1">
                      {guardianData?.total_pause_events || 0} pause events
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Stats Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label="Protected Contracts" value={guardianData?.protected_contract_count || 0}
                  icon={Shield} color="text-emerald-400" delay={0.05} />
                <StatCard label="Pause Events" value={guardianData?.total_pause_events || 0}
                  icon={Activity} color="text-blue-400" delay={0.1} />
                <StatCard label="Chain" value="Base"
                  icon={Lock} color="text-purple-400" delay={0.15} />
                <StatCard label="Owner" value="2-of-2 Safe"
                  icon={KeyRound} color="text-amber-400" delay={0.2} />
              </div>

              {/* Contract & Playbook Grid */}
              <div className="grid md:grid-cols-2 gap-5">
                {/* Protected Contracts */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
                  className="bg-white/[0.03] border border-white/5 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-emerald-400" /> Protected Contracts ({guardianContracts.length})
                  </h3>
                  <div className="space-y-2">
                    {guardianContracts.map((c, i) => (
                      <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/[0.03] last:border-0">
                        <div className="flex items-center gap-2.5">
                          <div className={`w-2 h-2 rounded-full ${c.has_native_pause ? 'bg-emerald-400' : 'bg-amber-400'}`} />
                          <div>
                            <span className="text-sm text-white font-medium">{c.name}</span>
                            <div className="text-[10px] text-gray-600 font-mono">{c.address?.slice(0, 10)}...{c.address?.slice(-6)}</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                            c.has_native_pause
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                              : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                          }`}>
                            {c.has_native_pause ? 'On-Chain Pause' : 'Backend Enforced'}
                          </span>
                          <a href={c.basescan_url} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-white">
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>

                {/* Emergency Playbook */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
                  className="bg-white/[0.03] border border-red-500/10 rounded-2xl p-5">
                  <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400" /> Emergency Pause Playbook
                  </h3>
                  <p className="text-xs text-gray-500 mb-4">
                    Response to Drift-style exploits. Execute as a single Safe batch transaction.
                  </p>
                  {guardianPlaybook?.steps?.map((step, i) => (
                    <div key={i} className="mb-3 last:mb-0">
                      <div className="flex items-start gap-2.5">
                        <span className="w-5 h-5 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center text-[10px] font-bold mt-0.5 shrink-0">
                          {step.step}
                        </span>
                        <div>
                          <div className="text-sm text-white font-medium">{step.action}</div>
                          <code className="text-[10px] text-cyan-400 font-mono block mt-0.5">{step.function}</code>
                          <p className="text-[10px] text-gray-500 mt-0.5">{step.effect}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div className="mt-4 pt-3 border-t border-white/5">
                    <p className="text-[10px] text-gray-600">Both multisig signers must approve. This is by design.</p>
                  </div>
                </motion.div>
              </div>

              {/* Sentinel Monitoring System */}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
                className="bg-white/[0.03] border border-white/5 rounded-2xl p-5" data-testid="sentinel-panel">
                <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-cyan-400" /> Sentinel — Private Email Alerts
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-white/[0.03] rounded-lg p-3 text-center">
                    <div className={`text-xs font-medium ${sentinelData?.configured ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {sentinelData?.configured ? 'Connected' : 'Not Configured'}
                    </div>
                    <div className="text-[10px] text-gray-600 mt-0.5">SendGrid Status</div>
                  </div>
                  <div className="bg-white/[0.03] rounded-lg p-3 text-center">
                    <div className="text-xs font-medium text-white">{sentinelData?.alerts_sent || 0}</div>
                    <div className="text-[10px] text-gray-600 mt-0.5">Alerts Sent</div>
                  </div>
                  <div className="bg-white/[0.03] rounded-lg p-3 text-center">
                    <div className="text-xs font-medium text-white">{sentinelData?.thresholds?.large_transfer_kutro?.toLocaleString() || '50,000'}</div>
                    <div className="text-[10px] text-gray-600 mt-0.5">Transfer Alert Threshold</div>
                  </div>
                  <div className="bg-white/[0.03] rounded-lg p-3 text-center">
                    <div className="text-xs font-medium text-white">{sentinelData?.thresholds?.large_unstake_kutro?.toLocaleString() || '25,000'}</div>
                    <div className="text-[10px] text-gray-600 mt-0.5">Unstake Alert Threshold</div>
                  </div>
                </div>
                {!sentinelData?.configured && (
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3">
                    <p className="text-xs text-amber-300 font-medium mb-2">SendGrid Not Configured</p>
                    <p className="text-[10px] text-gray-400">
                      Add <code>SENDGRID_API_KEY</code> and <code>SENDER_EMAIL</code> to backend .env and restart.
                      All alerts will be delivered to wayne@kojiru.com.
                    </p>
                  </div>
                )}
                {sentinelData?.configured && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        fetch(`${API}/api/sentinel/test-alert`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
                          .then(r => r.json()).then(d => alert(d.message));
                      }}
                      className="text-[10px] px-3 py-1.5 rounded-lg bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20 transition-colors"
                      data-testid="sentinel-test-btn"
                    >
                      Send Test Alert
                    </button>
                    <button
                      onClick={() => {
                        fetch(`${API}/api/sentinel/heartbeat`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } })
                          .then(r => r.json()).then(d => alert(d.message));
                      }}
                      className="text-[10px] px-3 py-1.5 rounded-lg bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-colors"
                      data-testid="sentinel-heartbeat-btn"
                    >
                      Send Heartbeat
                    </button>
                  </div>
                )}
              </motion.div>

              {/* Contract Link */}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
                className="bg-white/[0.02] border border-white/5 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <span className="text-xs text-gray-500">GuardianPause Contract</span>
                  <div className="font-mono text-xs text-white mt-0.5">{guardianData?.contract_address}</div>
                </div>
                <div className="flex items-center gap-2">
                  <a href={guardianData?.basescan_url} target="_blank" rel="noopener noreferrer"
                    className="text-xs px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 transition-colors flex items-center gap-1.5">
                    <ExternalLink className="w-3 h-3" /> Basescan
                  </a>
                  <a href={guardianData?.sourcify_url} target="_blank" rel="noopener noreferrer"
                    className="text-xs px-3 py-1.5 rounded-lg bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20 transition-colors flex items-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3" /> Sourcify
                  </a>
                </div>
              </motion.div>
            </div>
          ) : activeTab === 'home-loans' ? (
            /* Home Loans Tab */
            <HomeLoansTab />
          ) : activeTab === 'marketing' ? (
            /* Marketing Tab — Kojiru Home Ad */
            <MarketingTab />
          ) : activeTab === 'bd-leads' ? (
            /* BD Leads Tab — Agentic Payments Checkpoints PDF gate */
            <BDLeadsTab />
          ) : activeTab === 'pilot-signups' ? (
            <PilotSignupsInbox />
          ) : activeTab === 'claim-comments' ? (
            <ClaimCommentsInbox />
          ) : activeTab === 'settings' ? (
            /* Settings Tab */
            <div className="max-w-lg space-y-6" data-testid="settings-tab">
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white/[0.03] border border-white/5 rounded-2xl overflow-hidden"
              >
                <div className="px-6 py-4 border-b border-white/5 flex items-center gap-3">
                  <KeyRound className="w-5 h-5 text-blue-400" />
                  <div>
                    <h3 className="font-semibold">Change Password</h3>
                    <p className="text-xs text-gray-500">Update your admin account password</p>
                  </div>
                </div>
                <form onSubmit={handleChangePassword} className="p-6 space-y-4">
                  {pwMessage && (
                    <div
                      className={`p-3 rounded-lg border text-sm flex items-center gap-2 ${
                        pwMessage.type === 'success'
                          ? 'bg-green-500/10 border-green-500/20 text-green-400'
                          : 'bg-red-500/10 border-red-500/20 text-red-400'
                      }`}
                      data-testid="pw-change-message"
                    >
                      {pwMessage.type === 'success' ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertTriangle className="w-4 h-4 shrink-0" />}
                      {pwMessage.text}
                    </div>
                  )}
                  <div>
                    <label className="text-sm text-gray-400 mb-1.5 block">Current Password</label>
                    <input
                      type="password"
                      value={currentPw}
                      onChange={(e) => setCurrentPw(e.target.value)}
                      placeholder="Enter current password"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500/50 transition-colors"
                      required
                      data-testid="current-password-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1.5 block">New Password</label>
                    <input
                      type="password"
                      value={newPw}
                      onChange={(e) => setNewPw(e.target.value)}
                      placeholder="Enter new password (min 6 characters)"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500/50 transition-colors"
                      required
                      minLength={6}
                      data-testid="new-password-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 mb-1.5 block">Confirm New Password</label>
                    <input
                      type="password"
                      value={confirmPw}
                      onChange={(e) => setConfirmPw(e.target.value)}
                      placeholder="Re-enter new password"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500/50 transition-colors"
                      required
                      minLength={6}
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={pwLoading}
                    className="w-full h-11 bg-gradient-to-r from-blue-500 to-purple-500 hover:opacity-90"
                    data-testid="change-password-btn"
                  >
                    {pwLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Changing...
                      </span>
                    ) : (
                      <><Lock className="w-4 h-4 mr-2" /> Update Password</>
                    )}
                  </Button>
                </form>
              </motion.div>

              {/* Account Info */}
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
                className="bg-white/[0.03] border border-white/5 rounded-2xl p-6"
              >
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-green-400" /> Account Info
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-2 border-b border-white/5">
                    <span className="text-gray-500">Email</span>
                    <span className="text-white font-medium">{user?.email}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/5">
                    <span className="text-gray-500">Role</span>
                    <span className="text-orange-400 font-medium">Admin</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-white/5">
                    <span className="text-gray-500">User ID</span>
                    <code className="text-xs text-gray-400 font-mono">{user?.id}</code>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-500">Joined</span>
                    <span className="text-gray-400">{user?.created_at ? new Date(user.created_at).toLocaleDateString() : '--'}</span>
                  </div>
                </div>
              </motion.div>
            </div>
          ) : null}
        </div>
      </div>

      <Footer />
    </div>
  );
}
